import { maxEndDate } from '@/common/appConstants';
import { helpers } from '@vuelidate/validators';
import type { DirectiveBinding } from 'vue';

//returns validation error message
export const getErrorMsg = (valObj: any) => {
  const validationsToCheck = [
    'required',
    'requiredIfRef',
    'requiredIfFunction',
    'email',
    'maxLengthValue',
    'minLengthValue',
    'phoneValidator',
    'between',
    'decimal',
    'minValue',
    'maxValue',
    'alpha',
    'alphaNum',
    'integer',
    'numeric',
    'url',
    'or',
    'and',
    'not',
    'ipAddress',
    'macAddress',
    'dateOverlapValidator',
  ];

  for (let index = 0; index < validationsToCheck.length; index++) {
    const validation = validationsToCheck[index];
    if (Object.prototype.hasOwnProperty.call(valObj, validation)) {
      if (valObj[validation] && valObj[validation].$invalid) {
        return valObj[validation].$message;
      }
    }
  }
};

export const validateInputNumber = (event: KeyboardEvent, allowNegative: boolean = false) => {
  const validNumberKeys =
    event.ctrlKey ||
    event.altKey ||
    (47 < event.keyCode && event.keyCode < 58 && event.shiftKey === false) ||
    (95 < event.keyCode && event.keyCode < 106) ||
    event.keyCode === 8 ||
    event.keyCode === 9 ||
    (event.keyCode > 34 && event.keyCode < 40) ||
    event.keyCode === 46 ||
    event.key === '.' ||
    (allowNegative && event.key === '-');
  return validNumberKeys;
};

const validateInputCallback = (el: HTMLElement, allowNegative: boolean) => {
  const inputEl = el.querySelector('input') as HTMLInputElement;
  if (inputEl) {
    inputEl.addEventListener('keydown', (e: KeyboardEvent) => {
      const target = e.target as HTMLTextAreaElement;
      const initalValue = target.value;
      if (
        (e.key === '.' && initalValue.indexOf('.') >= 0) ||
        (allowNegative && e.key === '-' && initalValue !== '') ||
        !validateInputNumber(e, allowNegative)
      ) {
        e.stopPropagation();
        e.preventDefault();
      }

      let value = initalValue.replace(/[^0-9.-]*/g, '');
      let numValue = 0;
      if (value !== null) numValue = parseFloat(value);

      const maxval = target.getAttribute('maxval');
      if (maxval !== null) {
        if (numValue > parseFloat(maxval)) {
          value = maxval;
        }
      }
      target.value = value.toString();
      if (initalValue !== target.value) {
        e.stopPropagation();
        e.preventDefault();
      }
    });
  }
};

export const decimalOnlyDirective = {
  mounted(el: HTMLInputElement, binding: DirectiveBinding<number>) {
    const decimalPlaces = binding.value || 5; // Default to 5 decimal places if no value is provided
    const inputEl = el.querySelector('input') as HTMLInputElement;
    if (inputEl) {
      inputEl.addEventListener('keydown', (e: KeyboardEvent) => {
        const target = e.target as HTMLTextAreaElement;
        const initalValue = target.value;

        if ((e.key === '.' && initalValue.indexOf('.') >= 0) || !validateInputNumber(e, false)) {
          e.stopPropagation();
          e.preventDefault();
        }
      });

      inputEl.addEventListener('input', () => {
        const value = inputEl.value;
        if (value && value.includes('.') && !inputEl.getAttribute('maxlength')) {
          inputEl.setAttribute('maxlength', value.length + decimalPlaces + '');
        }

        if (!value && !value.includes('.')) {
          inputEl.removeAttribute('maxlength');
        }
      });
    }
  },
};

export const negativeDecimalOnlyDirective = {
  mounted(el: HTMLElement) {
    validateInputCallback(el, true);
  },
};

const validateMin = (inputEl: HTMLElement, value: number) => {
  if (inputEl.hasAttribute('min')) {
    const min = parseInt(inputEl.getAttribute('min') ?? '');

    if (min && value < min) {
      return false;
    }
  }
  return true;
};

const validateMax = (inputEl: HTMLElement, value: number) => {
  if (inputEl.hasAttribute('max')) {
    const max = parseInt(inputEl.getAttribute('max') ?? '');
    if (max && value > max) {
      return false;
    }
  }
  return true;
};

// Used to create validator rule function for decimal fields.

export const createDecimalValidator = (maxLength: number, scale: number, maxValue: number) => {
  return helpers.withMessage(
    // `Must be a number with a maximum of ${maxLength - scale} digits and ${scale} decimal places, and not exceed ${maxValue}`,
    `Must be maximum of ${maxLength - scale} digits and ${scale} decimal places.`,
    (value: number | string) => {
      if (value === '' || value === null) return true; // Allow empty values if not required

      // Check if the value is a number
      if (isNaN(value as number)) return false;

      const valueStr = value.toString();

      // Create regex for the specified maxLength and scale
      const regex = new RegExp(`^\\d{1,${maxLength - scale}}(\\.\\d{0,${scale}})?$`);
      const isValidFormat = regex.test(valueStr);
      const isValidRange = parseFloat(valueStr) <= maxValue;

      return isValidFormat && isValidRange;
    },
  );
};

/* 
Note :  Good to have maxLenght attribute for field. For max5Digits2Decimals we should have 6 as max length.
We will have 6 because 5 is max number length and 1 extra for dot(.)
*/
/*
// Length = 9, . Scale = 4
Precision (Prec): This is the total number of significant digits in the number. In your case, the precision is 18, meaning the number can have up to 18 digits in total.
Scale: This indicates the number of digits that can be to the right of the decimal point. In your case, the scale is 4, meaning you can have up to 4 digits after the decimal point.
Example : decimal(5,2) -> Precision = 5 and Scale = 2
The maximum value for the digits to the left of the decimal point is 999 (3 digits).
The maximum value for the digits to the right of the decimal point is 99 (2 digits).
Max Allowed for decimal(5,2) = 999.99
v-decimal-only="4" -> 4 is scale here
maxLength="10" -> Length + 1. 1 Extra to hold period(.) for decimal value
*/

export const max3Digits2Decimals = createDecimalValidator(5, 2, 999.99); // maxLength="6"
export const max4Digits4Decimals = createDecimalValidator(8, 4, 9999.9999); // maxLength="9"
export const max8Digits2Decimals = createDecimalValidator(10, 2, 99999999.99); // maxLength="11"

export const isEndDatePassed = (endDate: Date | string | undefined, effStartDate: Date | string) => {
  if (endDate === '' || endDate === '-' || !endDate) {
    endDate = maxEndDate;
  }
  const startDate = new Date(effStartDate);
  const parsedEndDate = new Date(endDate);
  return parsedEndDate >= startDate;
};

// Validate a zipcode for xxxxx and xxxxx-xxxx formats
export const isValidZipCode = (value: string): boolean => {
  if (!value) return true; // allow empty values (optional field)
  const zipRegex = /^\d{5}(-\d{4})?$/;

  return zipRegex.test(value);
};